/**
 * طبقة الاتصال لسجل الطلاب والمعلمين
 * (design/StudentsList.dc.html · StudentRecord.dc.html · StudentLeaves.dc.html · TeacherFile.dc.html).
 *
 * الراوتر جاهز في الباك إند (`app/routers/registry.py`) والملف ده بيغلّفه بس:
 *   • `GET   /students` · `/students/{id}` · `PATCH /students/{id}`
 *   • `GET   /families/{id}` · `PATCH /families/{id}`
 *   • `GET   /teachers` · `/teachers/{id}`
 *
 * ⛔ القواعد اللي الملف ده بيحرسها — كلها اتكسرت قبل كده في المشروع:
 *
 *   1) **ممنوع اختراع قيمة.** الخانة الفاضية بتفضل `null` وبتتعرض «—».
 *      مفيش «غير مذكور»، ومفيش `ar` بالافتراض، ومفيش منطقة زمنية مخمّنة.
 *      و`null` **مش** صفر: `students_count = null` معناها «محجوب عنك»،
 *      والصفر معناه «مفيش طلاب» — والخلط بينهم بيخلّي الشاشة تكدب.
 *
 *   2) **ممنوع الفشل الصامت.** أي رد مش قائمة/مش كائن بيرمي خطأ صريح
 *      ومعاه المسار في السجل — زي `api/team.ts` بالحرف.
 *
 *   3) **ممنوع فقدان صف.** الترقيم بيتقرا من `meta` بتاعة السيستم مش من
 *      الحالة المحلية، والعدّ المعروض هو `meta.total` — عشان «عرض 1–8 من
 *      199» تقول الحقيقة حتى لو السيستم صحّح الصفحة.
 *
 *   4) **التعديل بلا نسخة ممنوع.** الباك إند بيرمي 428 من غير `If-Match`،
 *      وده مقصود: الشاشة دي بتتفتح من كذا مشرفة في نفس الوقت على نفس
 *      قايمة «محتاج استكمال».
 */
import { api, ApiError } from "@/api/client";

/* ══════════════════════════════════════════════════════════════════
   أنواع
   ══════════════════════════════════════════════════════════════════ */

/** وسم جودة الداتا — «محتاج استكمال» وأسبابه بالعربي */
export type DataQuality = {
  needs_review: boolean;
  /** المفاتيح الإنجليزية — بتستخدم في ربط الوسم بالخانة اللي بتحلّه */
  issues: string[];
  /** نفس الترتيب بالظبط، بالعربي — ده اللي بيتعرض */
  issues_ar: string[];
  reviewed_at: string | null;
  reviewed_by: string | null;
  source: { sheet: string | null; row: number | null; imported_at: string | null } | null;
  /** النص الخام من الشيت — بيرجع لمين معاه صلاحية التصليح بس */
  raw: Record<string, string> | null;
};

export type PersonRef = { id: string; name_ar: string };

export type FamilyRef = {
  id: string;
  name_ar: string;
  family_code: string;
  country: string | null;
  timezone: string | null;
  primary_language: string | null;
  status: string | null;
  status_label_ar: string | null;
  /** تصنيف الأسرة — بييجي من السيرفر عشان صف الطالب ما يحتاجش نداء زيادة */
  is_vip: boolean;
};

export type TeacherRef = {
  id: string;
  name_ar: string;
  teacher_code: string;
  status: string | null;
};

export type StudentRef = {
  id: string;
  name_ar: string;
  student_code: string;
  family_id: string | null;
  status: string | null;
  status_label_ar: string | null;
};

export type StudentRow = {
  id: string;
  student_code: string;
  name_ar: string;
  name_en: string | null;
  birth_date: string | null;
  age_years: number | null;
  gender: string | null;
  family: FamilyRef | null;
  status: string | null;
  status_label_ar: string | null;
  status_reason_ar: string | null;
  status_changed_at: string | null;
  owner: PersonRef | null;
  last_contact_at: string | null;
  /** بييجي للمتوقفين بس — `null` للنشط يعني «مالوش معنى» مش «ناقص» */
  return_plan: ReturnPlan | null;
  data_quality: DataQuality;
  version: number;
};

export type EnrollmentRow = {
  id: string;
  student_id: string | null;
  student: StudentRef | null;
  family_id: string | null;
  subject: string | null;
  teacher: TeacherRef | null;
  status: string | null;
  status_label_ar: string | null;
  course_mode: string | null;
  /** ⛔ الساعات **نص** — الفلوت بيقرّب صامت، والتقريب في الرصيد = فلوس ضايعة */
  hours_balance: string | null;
  hours_consumed: string | null;
  contracted_hours: string | null;
  portal_hours_reported: string | null;
  hours_variance: string | null;
  started_at: string | null;
  ends_at: string | null;
  data_quality: DataQuality;
  version: number;
};

export type LeaveRow = {
  id: string;
  subject_type: string | null;
  subject_ref: string | null;
  type: string | null;
  type_label_ar: string | null;
  status: string | null;
  status_label_ar: string | null;
  start_date: string | null;
  end_date: string | null;
  /** 🔑 الفرق بين «النهاية مش محددة» و«الخانة مش متملّية» — الاتنين مش فراغ واحد */
  end_date_unknown: boolean;
  expected_return_at: string | null;
  reason_ar: string | null;
  hours_policy: string | null;
  report_source: string | null;
  next_check_at: string | null;
  data_quality: DataQuality;
  version: number;
};

/** إخوة الطالب في نفس الأسرة — الشاشة بتسمّيهم مش بتعدّهم */
/** خطة رجوع الطالب المتوقف — من التوقف المفتوح، مش من سجل الطالب */
export type ReturnPlan = {
  end_date: string | null;
  /** «النهاية مش معروفة» قرار متسجّل — مش خانة حد نسي يملاها */
  end_unknown: boolean;
  next_check_at: string | null;
};

export type SiblingRef = {
  id: string;
  name_ar: string;
  student_code: string;
  status: string | null;
  status_label_ar: string;
};

export type ContactRow = {
  id: string;
  family_id: string | null;
  family: FamilyRef | null;
  subject_name_raw: string | null;
  is_linked: boolean;
  student_id: string | null;
  channel: string | null;
  direction: string | null;
  at: string | null;
  by_user: PersonRef | null;
  summary_ar: string;
  parent_reply_ar: string | null;
  /** التواصل حصل قبل ما يتسجّل — الشاشة بتقول كده صراحةً */
  is_backdated: boolean;
  logged_at: string | null;
  data_quality: DataQuality;
  version: number;
};

export type GuardianOut = {
  name_ar: string;
  name_parts: { first: string | null; middle: string | null; last: string | null };
  phone_masked: string | null;
  phone_present: boolean | null;
  /** `false` = القسم ده مالوش حق يشوف الرقم أصلًا (الحقل اتشال من الاستعلام) */
  phone_visible: boolean;
  preferred_channel: string | null;
};

export type FamilyTag = { key: string; label_ar: string };

export type FamilyRow = {
  id: string;
  family_code: string;
  name_ar: string;
  guardian: GuardianOut;
  country: string | null;
  country_raw: string | null;
  timezone: string | null;
  primary_language: string | null;
  primary_language_ar: string | null;
  status: string | null;
  status_label_ar: string | null;
  status_reason_ar: string | null;
  children_count: number | null;
  owner: PersonRef | null;
  joined_at: string | null;
  /** الوسوم الخام — فيها وسوم نظام (`imported_support_sheet`) مش للعرض */
  tags: string[];
  is_vip: boolean;
  /** الوسوم **المعروضة** بس، بنصها العربي — القرار اتاخد في السيرفر */
  tags_ar: FamilyTag[];
  data_quality: DataQuality;
  version: number;
};

export type TeacherRow = {
  id: string;
  teacher_code: string;
  name_ar: string;
  name_en: string | null;
  gender: string | null;
  phone_masked: string | null;
  email: string | null;
  languages: string[];
  languages_ar: string[];
  subjects: string[];
  status: string | null;
  status_label_ar: string | null;
  status_reason_ar: string | null;
  status_changed_at: string | null;
  supervisor: PersonRef | null;
  /** اسم المشرفة لما لسه مالهاش حساب — التسجيل الذاتي بيربطها لاحقًا */
  supervisor_name_raw: string | null;
  /** تقدير أداء خام («نشط»/«متوسط»/«ضعيف») — ⚠️ **مش** حالة توظيف */
  performance_ar: string | null;
  telegram_linked: boolean;
  /** ⚠️ `null` = محجوب عنك (مش صفر) */
  students_count: number | null;
  stats_cache_computed_at: string | null;
  joined_at: string | null;
  data_quality: DataQuality;
  version: number;
};

export type StudentDetail = StudentRow & {
  enrollments: EnrollmentRow[];
  /** نتيجة حصة التقييم — `null` معناها «ما اتقيّمش» */
  evaluation: Record<string, unknown> | null;
  /** ⛔ «محجوب عن قسمك» ≠ «مفيش تقييم» */
  evaluation_visible: boolean;
  /** `false` = الاشتراكات محجوبة عن قسمك — **مش** «الطالب بلا اشتراكات» */
  enrollments_visible: boolean;
  leaves: LeaveRow[];
  leaves_visible: boolean;
  contact_log: ContactRow[];
  contact_log_visible: boolean;
  /** إخوة الطالب — فاضية معناها «ابن وحيد»، مش «مش محمّلين» */
  siblings: SiblingRef[];
};

export type FamilyDetail = FamilyRow & {
  children: StudentRow[];
  children_truncated: boolean;
  contact_log: ContactRow[];
  last_contact_at: string | null;
};

export type TeacherDetail = TeacherRow & {
  enrollments: EnrollmentRow[];
  enrollments_visible: boolean;
  students: StudentRef[];
  students_visible: boolean;
  /** أسماء وعدد بس (الإشراف الفني) — سجل الطالب نفسه مقفول عليهم */
  students_roster_only: boolean;
};

export type Page<T> = {
  rows: T[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  /** الحقول اللي البحث اتنفّذ عليها فعلًا — الشاشة بتقولها بدل ما تدّعي بحث شامل */
  searchedFields: string[];
  /** خاص بالمعلمين: عدد الطلاب مرئي ولا محجوب */
  countsVisible: boolean;
};

/* ══════════════════════════════════════════════════════════════════
   أدوات القراءة — ⛔ ممنوع «نجاح» على رد فاضي
   ══════════════════════════════════════════════════════════════════ */

function fail(path: string, reason: string): never {
  console.error(`[registry] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض البيانات" },
    null,
  );
}

type Raw = Record<string, unknown>;

function asArray(rows: unknown, path: string): Raw[] {
  if (!Array.isArray(rows)) fail(path, "الرد مش قائمة");
  return rows as Raw[];
}

function asObject(row: unknown, path: string): Raw {
  if (!row || typeof row !== "object" || Array.isArray(row)) fail(path, "الرد مش كائن");
  return row as Raw;
}

/**
 * ⚠️ **نقطة فرض قاعدة الأرقام اللاتينية.** كل نص جاي من السيستم بيعدّي من
 *    هنا، فأي رقم عربي-هندي (٠١٢٣) في نص عربي — أو في نص خام من الشيت —
 *    بيتحوّل للاتيني قبل ما يوصل الشاشة. القاعدة مقفولة على مستوى السجل كله
 *    بدل ما تتكرر في كل مكان عرض ويتنسى واحد.
 *    (التحويل بلا خسارة: نفس الرقم بمحارف تانية.)
 */
function str(v: unknown): string {
  return typeof v === "string" ? latinDigits(v) : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? latinDigits(v) : null;
}

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function intOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/**
 * الوسوم المعروضة زي ما السيرفر بعتها — بلا أي قرار عرض هنا.
 * ⛔ الواجهة **ما بتقررش** أنهي وسم يتعرض: الكتالوج في السيرفر، وتطبيق
 *    تاني للقاعدة دي هنا كان هيرسم «imported_support_sheet» على كل أسرة.
 */
function familyTags(v: unknown): FamilyTag[] {
  if (!Array.isArray(v)) return [];
  const out: FamilyTag[] = [];
  for (const item of v) {
    const o = obj(item);
    const key = str(o["key"]);
    const label = str(o["label_ar"]);
    if (key && label) out.push({ key, label_ar: label });
  }
  return out;
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function obj(v: unknown): Raw {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : {};
}

/** حارس: أي رقم عربي-هندي جاي من السيستم بيتحوّل للاتيني — قاعدة مقفولة */
export function latinDigits(text: string): string {
  return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

function quality(v: unknown): DataQuality {
  const q = obj(v);
  const source = q["source"] ? obj(q["source"]) : null;
  const raw = q["raw"] ? obj(q["raw"]) : null;
  return {
    needs_review: q["needs_review"] === true,
    issues: strList(q["issues"]),
    issues_ar: strList(q["issues_ar"]).map(latinDigits),
    reviewed_at: strOrNull(q["reviewed_at"]),
    reviewed_by: strOrNull(q["reviewed_by"]),
    source: source
      ? {
          sheet: strOrNull(source["sheet"]),
          row: numOrNull(source["row"]),
          imported_at: strOrNull(source["imported_at"]),
        }
      : null,
    // النص الخام من الشيت — بيتعرض زي ما هو، بأرقام لاتينية زي باقي الشاشة
    raw: raw
      ? Object.fromEntries(
          Object.entries(raw).map(([k, val]) => [latinDigits(k), latinDigits(String(val))]),
        )
      : null,
  };
}

function personRef(v: unknown): PersonRef | null {
  if (!v) return null;
  const o = obj(v);
  const id = str(o["id"]);
  if (!id) return null;
  return { id, name_ar: str(o["name_ar"]) };
}

function familyRef(v: unknown): FamilyRef | null {
  if (!v) return null;
  const o = obj(v);
  const id = str(o["id"]);
  if (!id) return null;
  return {
    id,
    name_ar: str(o["name_ar"]),
    family_code: str(o["family_code"]),
    country: strOrNull(o["country"]),
    timezone: strOrNull(o["timezone"]),
    primary_language: strOrNull(o["primary_language"]),
    status: strOrNull(o["status"]),
    status_label_ar: strOrNull(o["status_label_ar"]),
    is_vip: o["is_vip"] === true,
  };
}

function teacherRef(v: unknown): TeacherRef | null {
  if (!v) return null;
  const o = obj(v);
  const id = str(o["id"]);
  if (!id) return null;
  return {
    id,
    name_ar: str(o["name_ar"]),
    teacher_code: str(o["teacher_code"]),
    status: strOrNull(o["status"]),
  };
}

function studentRef(v: unknown): StudentRef | null {
  if (!v) return null;
  const o = obj(v);
  const id = str(o["id"]);
  if (!id) return null;
  return {
    id,
    name_ar: str(o["name_ar"]),
    student_code: str(o["student_code"]),
    family_id: strOrNull(o["family_id"]),
    status: strOrNull(o["status"]),
    status_label_ar: strOrNull(o["status_label_ar"]),
  };
}

function studentRow(raw: Raw, path: string): StudentRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "طالب بلا معرّف في الرد");
  return {
    id,
    student_code: str(raw["student_code"]),
    name_ar: str(raw["name_ar"]),
    name_en: strOrNull(raw["name_en"]),
    birth_date: strOrNull(raw["birth_date"]),
    age_years: numOrNull(raw["age_years"]),
    gender: strOrNull(raw["gender"]),
    family: familyRef(raw["family"]),
    status: strOrNull(raw["status"]),
    status_label_ar: strOrNull(raw["status_label_ar"]),
    status_reason_ar: strOrNull(raw["status_reason_ar"]),
    status_changed_at: strOrNull(raw["status_changed_at"]),
    owner: personRef(raw["owner"]),
    last_contact_at: strOrNull(raw["last_contact_at"]),
    return_plan: raw["return_plan"]
      ? (() => {
          const rp = asObject(raw["return_plan"], path);
          return {
            end_date: strOrNull(rp["end_date"]),
            end_unknown: rp["end_unknown"] === true,
            next_check_at: strOrNull(rp["next_check_at"]),
          };
        })()
      : null,
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

function enrollmentRow(raw: Raw, path: string): EnrollmentRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "اشتراك بلا معرّف في الرد");
  return {
    id,
    student_id: strOrNull(raw["student_id"]),
    student: studentRef(raw["student"]),
    family_id: strOrNull(raw["family_id"]),
    subject: strOrNull(raw["subject"]),
    teacher: teacherRef(raw["teacher"]),
    status: strOrNull(raw["status"]),
    status_label_ar: strOrNull(raw["status_label_ar"]),
    course_mode: strOrNull(raw["course_mode"]),
    hours_balance: strOrNull(raw["hours_balance"]),
    hours_consumed: strOrNull(raw["hours_consumed"]),
    contracted_hours: strOrNull(raw["contracted_hours"]),
    portal_hours_reported: strOrNull(raw["portal_hours_reported"]),
    hours_variance: strOrNull(raw["hours_variance"]),
    started_at: strOrNull(raw["started_at"]),
    ends_at: strOrNull(raw["ends_at"]),
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

function leaveRow(raw: Raw, path: string): LeaveRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "توقف بلا معرّف في الرد");
  return {
    id,
    subject_type: strOrNull(raw["subject_type"]),
    subject_ref: strOrNull(raw["subject_ref"]),
    type: strOrNull(raw["type"]),
    type_label_ar: strOrNull(raw["type_label_ar"]),
    status: strOrNull(raw["status"]),
    status_label_ar: strOrNull(raw["status_label_ar"]),
    start_date: strOrNull(raw["start_date"]),
    end_date: strOrNull(raw["end_date"]),
    end_date_unknown: raw["end_date_unknown"] === true,
    expected_return_at: strOrNull(raw["expected_return_at"]),
    reason_ar: strOrNull(raw["reason_ar"]),
    hours_policy: strOrNull(raw["hours_policy"]),
    report_source: strOrNull(raw["report_source"]),
    next_check_at: strOrNull(raw["next_check_at"]),
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

function contactRow(raw: Raw, path: string): ContactRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "قيد تواصل بلا معرّف في الرد");
  return {
    id,
    family_id: strOrNull(raw["family_id"]),
    family: familyRef(raw["family"]),
    subject_name_raw: strOrNull(raw["subject_name_raw"]),
    is_linked: raw["is_linked"] === true,
    student_id: strOrNull(raw["student_id"]),
    channel: strOrNull(raw["channel"]),
    direction: strOrNull(raw["direction"]),
    at: strOrNull(raw["at"]),
    by_user: personRef(raw["by_user"]),
    summary_ar: str(raw["summary_ar"]),
    parent_reply_ar: strOrNull(raw["parent_reply_ar"]),
    is_backdated: raw["is_backdated"] === true,
    logged_at: strOrNull(raw["logged_at"]),
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

function familyRow(raw: Raw, path: string): FamilyRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "أسرة بلا معرّف في الرد");
  const g = obj(raw["guardian"]);
  const parts = obj(g["name_parts"]);
  return {
    id,
    family_code: str(raw["family_code"]),
    name_ar: str(raw["name_ar"]),
    guardian: {
      name_ar: str(g["name_ar"]),
      name_parts: {
        first: strOrNull(parts["first"]),
        middle: strOrNull(parts["middle"]),
        last: strOrNull(parts["last"]),
      },
      phone_masked: strOrNull(g["phone_masked"]),
      phone_present: typeof g["phone_present"] === "boolean" ? g["phone_present"] : null,
      phone_visible: g["phone_visible"] === true,
      preferred_channel: strOrNull(g["preferred_channel"]),
    },
    country: strOrNull(raw["country"]),
    country_raw: strOrNull(raw["country_raw"]),
    timezone: strOrNull(raw["timezone"]),
    primary_language: strOrNull(raw["primary_language"]),
    primary_language_ar: strOrNull(raw["primary_language_ar"]),
    status: strOrNull(raw["status"]),
    status_label_ar: strOrNull(raw["status_label_ar"]),
    status_reason_ar: strOrNull(raw["status_reason_ar"]),
    children_count: numOrNull(raw["children_count"]),
    owner: personRef(raw["owner"]),
    joined_at: strOrNull(raw["joined_at"]),
    tags: strList(raw["tags"]),
    is_vip: raw["is_vip"] === true,
    tags_ar: familyTags(raw["tags_ar"]),
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

function teacherRow(raw: Raw, path: string): TeacherRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "معلم بلا معرّف في الرد");
  return {
    id,
    teacher_code: str(raw["teacher_code"]),
    name_ar: str(raw["name_ar"]),
    name_en: strOrNull(raw["name_en"]),
    gender: strOrNull(raw["gender"]),
    phone_masked: strOrNull(raw["phone_masked"]),
    email: strOrNull(raw["email"]),
    languages: strList(raw["languages"]),
    languages_ar: strList(raw["languages_ar"]),
    subjects: strList(raw["subjects"]),
    status: strOrNull(raw["status"]),
    status_label_ar: strOrNull(raw["status_label_ar"]),
    status_reason_ar: strOrNull(raw["status_reason_ar"]),
    status_changed_at: strOrNull(raw["status_changed_at"]),
    supervisor: personRef(raw["supervisor"]),
    supervisor_name_raw: str(raw["supervisor_name_raw"]),
    performance_ar: str(raw["performance_ar"]),
    telegram_linked: raw["telegram_linked"] === true,
    // ⛔ `numOrNull` مش `?? 0` — المحجوب لازم يفضل `null`
    students_count: numOrNull(raw["students_count"]),
    stats_cache_computed_at: strOrNull(raw["stats_cache_computed_at"]),
    joined_at: strOrNull(raw["joined_at"]),
    data_quality: quality(raw["data_quality"]),
    version: intOr(raw["version"], 1),
  };
}

/** الترقيم من رد السيستم — مش من الحالة المحلية */
function pageOf<T>(rows: T[], metaRaw: unknown, fallbackPage: number, fallbackPerPage: number): Page<T> {
  const m = obj(metaRaw);
  const perPage = intOr(m["per_page"], fallbackPerPage);
  const total = intOr(m["total"], rows.length);
  return {
    rows,
    total,
    page: intOr(m["page"], fallbackPage),
    perPage,
    totalPages: intOr(m["total_pages"], Math.max(Math.ceil(total / Math.max(perPage, 1)), 1)),
    searchedFields: strList(m["searched_fields"]),
    // غياب المفتاح ⇒ مرئي (المسارات التانية مش بترجّعه أصلًا)
    countsVisible: m["students_count_visible"] !== false,
  };
}

/* ══════════════════════════════════════════════════════════════════
   شرايح شاشة الطلاب — الفلاتر
   ══════════════════════════════════════════════════════════════════

   ⚠️ نص الشريحة **من كتالوج السيستم** (`STUDENT_STATUS_AR` في
      `services/registry.py`) مش من نص الموك. السبب: عمود «الحالة» في
      الجدول بيعرض `status_label_ar` الجاي من السيستم — فلو الشريحة
      مكتوب عليها «غياب متكرر» والصف مكتوب عليه «كتير الغياب»، الموظفة
      هتفتكر إن الفلتر رجّع حاجة تانية. كلمة واحدة لكل حالة.
*/

export type StudentSegment =
  | "all"
  | "active"
  | "new"
  | "frequent_absence"
  | "suspended_by_academy"
  | "paused"
  | "needs_review"
  | "vip";

export type SegmentDef = { key: StudentSegment; chip: string; title: string };

export const STUDENT_SEGMENTS: readonly SegmentDef[] = [
  { key: "all", chip: "الكل", title: "كل الطلاب" },
  { key: "active", chip: "نشط", title: "الطلاب النشطين" },
  { key: "new", chip: "جديد", title: "الطلاب الجدد" },
  { key: "frequent_absence", chip: "كتير الغياب", title: "طلاب كتير الغياب" },
  { key: "suspended_by_academy", chip: "موقوف من الأكاديمية", title: "الموقوفون من الأكاديمية" },
  { key: "paused", chip: "متوقف", title: "الطلاب المتوقفين" },
  // 🔴 الشريحة اللي بتخلّي الفريق ينضّف الداتا وهو شغّال
  { key: "needs_review", chip: "محتاج استكمال", title: "سجلات محتاجة استكمال" },
  // 🔑 التصنيف وسم على **الأسرة** — الشريحة بتفلتر الطلاب من خلالها
  { key: "vip", chip: "VIP", title: "طلاب أسر VIP" },
];

function segmentQuery(segment: StudentSegment): Record<string, string | boolean | undefined> {
  if (segment === "all") return {};
  if (segment === "needs_review") return { needs_review: true };
  // ⛔ الاستثناء ده إجباري: الافتراضي تحت بيبعت `status=<الشريحة>`، و«vip»
  //    مش حالة طالب — السيرفر كان هيلاقي قيمة بره كتالوج الحالات.
  if (segment === "vip") return { family_tag: "vip" };
  return { status: segment };
}

export type TeacherSegment = "all" | "active" | "on_leave" | "suspended" | "left" | "needs_review";

export const TEACHER_SEGMENTS: readonly { key: TeacherSegment; chip: string; title: string }[] = [
  { key: "all", chip: "الكل", title: "كل المعلمين" },
  { key: "active", chip: "نشط", title: "المعلمون النشطون" },
  { key: "on_leave", chip: "في إجازة", title: "المعلمون في إجازة" },
  { key: "suspended", chip: "موقوف", title: "المعلمون الموقوفون" },
  { key: "left", chip: "ساب الأكاديمية", title: "اللي سابوا الأكاديمية" },
  { key: "needs_review", chip: "محتاج استكمال", title: "سجلات محتاجة استكمال" },
];

function teacherSegmentQuery(segment: TeacherSegment): Record<string, string | boolean | undefined> {
  if (segment === "all") return {};
  if (segment === "needs_review") return { needs_review: true };
  return { status: segment };
}

/* ══════════════════════════════════════════════════════════════════
   القراءة
   ══════════════════════════════════════════════════════════════════ */

export async function fetchStudents(opts: {
  segment: StudentSegment;
  page: number;
  perPage: number;
  q?: string | null;
  teacherId?: string | null;
}): Promise<Page<StudentRow>> {
  const path = "/students";
  const res = await api.get<unknown>(path, {
    query: {
      ...segmentQuery(opts.segment),
      q: opts.q?.trim() || undefined,
      teacher_id: opts.teacherId || undefined,
      page: opts.page,
      per_page: opts.perPage,
    },
  });
  const rows = asArray(res.data, path).map((raw) => studentRow(raw, path));
  return pageOf(rows, res.meta, opts.page, opts.perPage);
}

/**
 * عدّاد كل شريحة — طلب خفيف (`per_page=1`) لكل واحدة، والرقم من `meta.total`.
 *
 * ⛔ الشريحة اللي عدّادها فشل بترجع `null` والشاشة بتعرضها **بلا رقم** —
 *    مش صفر. الصفر بيقول «مفيش»، والفشل بيقول «مش عارفين».
 * ⚠️ ومجموع الشرايح **مش** بيساوي «الكل» عمدًا: فيه حالات مش معروضة
 *    (تحت التقييم · إيقاف مالي · منسحب)، و«محتاج استكمال» بتتقاطع مع
 *    الباقي. عشان كده الشاشة ما بتدّعيش أي تطابق.
 */
export async function fetchStudentCounts(opts: {
  q?: string | null;
  teacherId?: string | null;
}): Promise<Record<StudentSegment, number | null>> {
  const out = {} as Record<StudentSegment, number | null>;
  await Promise.all(
    STUDENT_SEGMENTS.map(async (seg) => {
      try {
        const page = await fetchStudents({
          segment: seg.key,
          page: 1,
          perPage: 1,
          q: opts.q ?? null,
          teacherId: opts.teacherId ?? null,
        });
        out[seg.key] = page.total;
      } catch (err) {
        console.error(`[registry] عدّاد شريحة «${seg.chip}» فشل`, err);
        out[seg.key] = null;
      }
    }),
  );
  return out;
}

export async function fetchStudent(studentId: string): Promise<StudentDetail> {
  const path = `/students/${studentId}`;
  const res = await api.get<unknown>(path);
  const raw = asObject(res.data, path);
  return {
    ...studentRow(raw, path),
    enrollments: asArray(raw["enrollments"] ?? [], path).map((r) => enrollmentRow(r, path)),
    enrollments_visible: raw["enrollments_visible"] !== false,
    // 🔴 **الخادم بقى بيرجّع النتيجة والواجهة مكانتش بترسمها.** المقيّمة
    //    بتكتب مستوى الطالب وهواياته وتوصياتها، والمشرفة اللي هتستلمه
    //    ما بتشوفش ولا حرف — نفس نمط «مكتوب وما اتعرضش».
    evaluation: (raw["evaluation"] ?? null) as Record<string, unknown> | null,
    evaluation_visible: raw["evaluation_visible"] !== false,
    leaves: asArray(raw["leaves"] ?? [], path).map((r) => leaveRow(r, path)),
    leaves_visible: raw["leaves_visible"] !== false,
    contact_log: asArray(raw["contact_log"] ?? [], path).map((r) => contactRow(r, path)),
    contact_log_visible: raw["contact_log_visible"] !== false,
    siblings: asArray(raw["siblings"] ?? [], path).map((r) => {
      const s = asObject(r, path);
      return {
        id: str(s["id"]),
        name_ar: str(s["name_ar"]),
        student_code: str(s["student_code"]),
        status: strOrNull(s["status"]),
        status_label_ar: str(s["status_label_ar"]),
      };
    }),
  };
}

export async function fetchFamily(familyId: string): Promise<FamilyDetail> {
  const path = `/families/${familyId}`;
  const res = await api.get<unknown>(path);
  const raw = asObject(res.data, path);
  return {
    ...familyRow(raw, path),
    children: asArray(raw["children"] ?? [], path).map((r) => studentRow(r, path)),
    children_truncated: raw["children_truncated"] === true,
    contact_log: asArray(raw["contact_log"] ?? [], path).map((r) => contactRow(r, path)),
    last_contact_at: strOrNull(raw["last_contact_at"]),
  };
}

/**
 * بحث سريع عن أسرة بالاسم — لمنتقي الأسرة في شاشة الحسابات.
 *
 * 🔑 **بيرجّع نتايج البحث زي ما هي، بلا أي ترتيب بالتشابه**: الموظف هو
 *    اللي بيقرر. أي ترتيب «الأقرب الأول» بيدفع الإنسان يدوس على أول صف،
 *    وده تخمين بواجهة لطيفة — وهو بالظبط اللي الشاشة اتعملت عشان تمنعه.
 */
export async function searchFamilies(
  q: string,
  limit = 12,
): Promise<{ id: string; name_ar: string; family_code: string; country: string | null }[]> {
  const path = "/families";
  const res = await api.get<unknown>(path, { query: { q, page: 1, per_page: limit } });
  return asArray(res.data, path).map((r) => {
    const o = obj(r);
    return {
      id: str(o["id"]),
      name_ar: str(o["name_ar"]),
      family_code: str(o["family_code"]),
      country: strOrNull(o["country"]),
    };
  });
}

export async function fetchTeachers(opts: {
  segment: TeacherSegment;
  page: number;
  perPage: number;
  q?: string | null;
}): Promise<Page<TeacherRow>> {
  const path = "/teachers";
  const res = await api.get<unknown>(path, {
    query: {
      ...teacherSegmentQuery(opts.segment),
      q: opts.q?.trim() || undefined,
      page: opts.page,
      per_page: opts.perPage,
    },
  });
  const rows = asArray(res.data, path).map((raw) => teacherRow(raw, path));
  return pageOf(rows, res.meta, opts.page, opts.perPage);
}

export async function fetchTeacherCounts(opts: {
  q?: string | null;
}): Promise<Record<TeacherSegment, number | null>> {
  const out = {} as Record<TeacherSegment, number | null>;
  await Promise.all(
    TEACHER_SEGMENTS.map(async (seg) => {
      try {
        const page = await fetchTeachers({
          segment: seg.key,
          page: 1,
          perPage: 1,
          q: opts.q ?? null,
        });
        out[seg.key] = page.total;
      } catch (err) {
        console.error(`[registry] عدّاد شريحة المعلمين «${seg.chip}» فشل`, err);
        out[seg.key] = null;
      }
    }),
  );
  return out;
}

/** الحالات اللي الإشراف بيكتبها بالإيد — لازم تطابق `MANUAL_TEACHER_STATUSES` */
export const MANUAL_TEACHER_STATUSES: readonly { value: string; label_ar: string }[] = [
  { value: "active", label_ar: "نشط" },
  { value: "on_leave", label_ar: "في إجازة" },
  { value: "suspended", label_ar: "موقوف" },
  { value: "left", label_ar: "ساب الأكاديمية" },
];

export async function setTeacherStatus(
  teacherId: string,
  version: number,
  body: { status: string; reason_ar: string },
): Promise<{ row: TeacherRow; toastAr: string }> {
  const path = `/teachers/${teacherId}/status`;
  const res = await api.post<unknown>(path, body, { ifMatch: version });
  const raw = asObject(res.data, path);
  return { row: teacherRow(raw, path), toastAr: str(raw["toast_ar"]) || "الحالة اتغيّرت" };
}

/** دمج سجل مكرر في السجل الصح — كل الطلاب والإجازات بتتنقل والمكرر بيتمسح. */
export async function mergeTeacherInto(
  duplicateId: string,
  targetId: string,
  reasonAr: string,
): Promise<{ moved: Record<string, number>; toastAr: string }> {
  const path = `/teachers/${duplicateId}/merge-into`;
  const res = await api.post<unknown>(path, { target_teacher_id: targetId, reason_ar: reasonAr });
  const raw = asObject(res.data, path);
  const moved = obj(raw["moved"]);
  return {
    moved: Object.fromEntries(Object.entries(moved).map(([k, v]) => [k, Number(v) || 0])),
    toastAr: str(raw["toast_ar"]) || "اتدمج",
  };
}

/** مسح ناعم بسبب — للأسماء المكررة. اللي عنده طلاب نشطين السيستم بيرفضه. */
export async function archiveTeacher(teacherId: string, reasonAr: string): Promise<void> {
  await api.post<void>(`/teachers/${teacherId}/archive`, { reason_ar: reasonAr });
}

export async function fetchTeacher(teacherId: string): Promise<TeacherDetail> {
  const path = `/teachers/${teacherId}`;
  const res = await api.get<unknown>(path);
  const raw = asObject(res.data, path);
  const students = Array.isArray(raw["students"])
    ? (raw["students"] as unknown[]).map(studentRef).filter((s): s is StudentRef => s !== null)
    : [];
  return {
    ...teacherRow(raw, path),
    enrollments: asArray(raw["enrollments"] ?? [], path).map((r) => enrollmentRow(r, path)),
    enrollments_visible: raw["enrollments_visible"] !== false,
    students,
    students_visible: raw["students_visible"] !== false,
    students_roster_only: raw["students_roster_only"] === true,
  };
}

/**
 * قائمة المعلمين للفلترة — طلب واحد خفيف.
 * ⚠️ مش بتتستخدم لعرض «معلم الطالب» في الجدول: رد `GET /students` مفيهوش
 *    معلم أصلًا (شوف التعليق فوق `TEACHER_COLUMN_NOTE_AR`).
 */
export async function fetchTeacherOptions(): Promise<TeacherRow[]> {
  const page = await fetchTeachers({ segment: "all", page: 1, perPage: 100 });
  return [...page.rows].sort((a, b) => a.name_ar.localeCompare(b.name_ar, "ar"));
}

/**
 * 🔴 عمود «المعلم الحالي» اللي في التصميم **مش قابل للتعبئة من رد القايمة**:
 *    `GET /students` بيرجّع الطالب وأسرته وبس — الاشتراك (وفيه المعلم) بيرجع
 *    في صفحة الطالب لوحدها. الاختيارات كانت:
 *      • عمود كله «—»            ⇒ عمود ميت بيوحي إن الطلاب بلا معلمين.
 *      • نداء تفاصيل لكل صف      ⇒ 8 طلبات لكل صفحة على داتا تقيلة.
 *      • **فلتر بالمعلم**        ⇒ طلب واحد، والمعلومة الحقيقية في السجل.
 *    اخترنا التالت، والغياب **معلن في الشاشة** مش متبلّع.
 */
export const TEACHER_COLUMN_NOTE_AR =
  "معلم الطالب بيبان جوّه سجل الطالب — رد القايمة مفيهوش الاشتراكات. للفرز بالمعلم استخدم الفلتر.";

/* ══════════════════════════════════════════════════════════════════
   🔴 الاستكمال في مكانه — أهم مسار في الشاشة
   ══════════════════════════════════════════════════════════════════

   الفريق بيصلّح الداتا **وهو بيستخدمها**، بدل ما يرجع للشيت. الوسم بيختفي
   لما السيستم يتأكد إن النقص اتحل فعلًا (المحلّلات في
   `services/registry.py`) — مش لمجرد إن الخانة اتلمست.
*/

/** خانة استكمال واحدة زي ما الشاشة بتعرضها */
export type FixField = {
  /** اسم الحقل في جسم الـPATCH */
  name: string;
  label_ar: string;
  hint_ar: string;
  kind: "text" | "language" | "country" | "timezone" | "phone";
  /** على أي مورد بيتبعت التعديل */
  target: "student" | "family";
};

const FAMILY_NAME_FIELDS: FixField[] = [
  { name: "guardian_name_first", label_ar: "الاسم الأول لولي الأمر", hint_ar: "", kind: "text", target: "family" },
  { name: "guardian_name_middle", label_ar: "اسم الأب", hint_ar: "", kind: "text", target: "family" },
  { name: "guardian_name_last", label_ar: "اسم العائلة", hint_ar: "", kind: "text", target: "family" },
];

/**
 * وسم النقص ⇒ الخانات اللي بتحلّه.
 * ⛔ الوسم اللي مالوش خانة هنا **مالوش حل من الشاشة** — بيتعرض بسببه
 *    ومكتوب جنبه إنه محتاج تدخل تاني، مش بيتخبّى ولا بيتشال.
 */
export const STUDENT_FIXES: Record<string, FixField[]> = {
  name_incomplete: [
    {
      name: "name_ar",
      label_ar: "اسم الطالب كامل",
      hint_ar: "تلات أجزاء على الأقل — الاسم واسم الأب واسم العائلة",
      kind: "text",
      target: "student",
    },
  ],
};

export const FAMILY_FIXES: Record<string, FixField[]> = {
  no_phone: [
    {
      name: "guardian_phone_e164",
      label_ar: "رقم ولي الأمر",
      hint_ar: "بصيغة دولية كاملة تبدأ بـ+ ومعاها مفتاح الدولة",
      kind: "phone",
      target: "family",
    },
  ],
  phone_unparseable: [
    {
      name: "guardian_phone_e164",
      label_ar: "رقم ولي الأمر",
      hint_ar: "بصيغة دولية كاملة تبدأ بـ+ ومعاها مفتاح الدولة",
      kind: "phone",
      target: "family",
    },
  ],
  no_timezone: [
    {
      name: "timezone",
      label_ar: "المنطقة الزمنية",
      // ⛔ ممنوع نخمّنها من الدولة — كندا فيها ٦ مناطق، والتخمين = حصة في وقت غلط
      hint_ar: "بصيغة IANA زي America/Toronto — ⛔ ما تتخمّنش من الدولة",
      kind: "timezone",
      target: "family",
    },
  ],
  country_unmapped: [
    {
      name: "country",
      label_ar: "الدولة",
      hint_ar: "اختار الدولة من القايمة",
      kind: "country",
      target: "family",
    },
  ],
  language_unknown: [
    {
      name: "primary_language",
      label_ar: "لغة التواصل",
      hint_ar: "لغة الأسرة الأساسية — ⛔ مفيش لغة افتراضية",
      kind: "language",
      target: "family",
    },
  ],
  name_incomplete: FAMILY_NAME_FIELDS,
};

/** الوسوم اللي بتتحل من الشاشة دي — الباقي بيتعرض مع سبب عدم الحل */
export function fixesFor(target: "student" | "family", issue: string): FixField[] {
  const table = target === "student" ? STUDENT_FIXES : FAMILY_FIXES;
  return table[issue] ?? [];
}

/** ليه الوسم ده مش بيتحل من هنا — نص صريح بدل ما يبان كأننا نسيناه */
export const UNFIXABLE_HERE_AR: Record<string, string> = {
  name_composite: "الخانة فيها أكتر من شخص — الحل فصل السجل لسجلين، مش تعديل خانة.",
  hours_unparseable: "الساعات بتتصلّح من شاشة الحسابات.",
  rate_unparseable: "السعر بيتصلّح من شاشة الحسابات.",
  date_unparseable: "التاريخ بيتصلّح من السجل اللي جاي منه.",
  no_reason: "السبب بيتكتب من شاشة التوقفات.",
  no_start_date: "تاريخ البداية بيتكتب من شاشة التوقفات.",
  subject_unspecified: "المادة بتتحدد من شاشة الاشتراك.",
  unmatched_family: "القيد ده محتاج ربط بأسرة من شاشة سجل التواصل.",
  unmatched_teacher: "المعلم محتاج ربط من شاشة الاشتراك.",
};

/**
 * الدول اللي بنتعامل معاها — نفس القايمة بالحرف في `backend/app/services/countries.py`.
 * القيمة هي رمز ISO، والاسم عربي. الموظفة تختار ومتكتبش رمز.
 */
export const COUNTRY_OPTIONS: readonly { value: string; label_ar: string }[] = [
  { value: "EG", label_ar: "مصر" }, { value: "SA", label_ar: "السعودية" },
  { value: "AE", label_ar: "الإمارات" }, { value: "KW", label_ar: "الكويت" },
  { value: "QA", label_ar: "قطر" }, { value: "BH", label_ar: "البحرين" },
  { value: "OM", label_ar: "عُمان" }, { value: "JO", label_ar: "الأردن" },
  { value: "PS", label_ar: "فلسطين" }, { value: "LB", label_ar: "لبنان" },
  { value: "SY", label_ar: "سوريا" }, { value: "IQ", label_ar: "العراق" },
  { value: "YE", label_ar: "اليمن" }, { value: "LY", label_ar: "ليبيا" },
  { value: "TN", label_ar: "تونس" }, { value: "DZ", label_ar: "الجزائر" },
  { value: "MA", label_ar: "المغرب" }, { value: "SD", label_ar: "السودان" },
  { value: "SO", label_ar: "الصومال" }, { value: "MR", label_ar: "موريتانيا" },
  { value: "US", label_ar: "أمريكا" }, { value: "CA", label_ar: "كندا" },
  { value: "GB", label_ar: "بريطانيا" }, { value: "DE", label_ar: "ألمانيا" },
  { value: "FR", label_ar: "فرنسا" }, { value: "IT", label_ar: "إيطاليا" },
  { value: "ES", label_ar: "إسبانيا" }, { value: "NL", label_ar: "هولندا" },
  { value: "SE", label_ar: "السويد" }, { value: "NO", label_ar: "النرويج" },
  { value: "DK", label_ar: "الدنمارك" }, { value: "BE", label_ar: "بلجيكا" },
  { value: "CH", label_ar: "سويسرا" }, { value: "AT", label_ar: "النمسا" },
  { value: "IE", label_ar: "أيرلندا" }, { value: "TR", label_ar: "تركيا" },
  { value: "AU", label_ar: "أستراليا" }, { value: "NZ", label_ar: "نيوزيلندا" },
  { value: "MY", label_ar: "ماليزيا" }, { value: "ID", label_ar: "إندونيسيا" },
  { value: "PK", label_ar: "باكستان" }, { value: "IN", label_ar: "الهند" },
  { value: "ZA", label_ar: "جنوب أفريقيا" }, { value: "NG", label_ar: "نيجيريا" },
  { value: "KE", label_ar: "كينيا" },
];

export const LANGUAGE_OPTIONS: readonly { value: string; label_ar: string }[] = [
  { value: "ar", label_ar: "عربي" },
  { value: "en", label_ar: "إنجليزي" },
  { value: "fr", label_ar: "فرنساوي" },
];

/**
 * الحالات اللي الشاشة بتقدر **تحطها** — النصوص مطابقة حرفيًا لـ
 * `STUDENT_STATUS_AR` في `services/registry.py` (فيه تست بيقارن الاتنين).
 * لو القايمة كتبت «موقوف» والشارة بتقول «موقوف من الأكاديمية»، الموظفة
 * تفتكر إنهم حالتان.
 * `elevated` = قرار إدارة، محتاج `student.archive` — والباك إند بيرفضها
 * بـ`FORBIDDEN_SCOPE` حتى لو الشاشة عرضتها بالغلط.
 */
export const MANUAL_STUDENT_STATUSES: readonly {
  value: string;
  label_ar: string;
  elevated: boolean;
}[] = [
  { value: "active", label_ar: "نشط", elevated: false },
  { value: "paused", label_ar: "متوقف", elevated: false },
  { value: "suspended_by_academy", label_ar: "موقوف من الأكاديمية", elevated: false },
  { value: "financial_hold", label_ar: "إيقاف مالي", elevated: true },
  { value: "withdrawn", label_ar: "منسحب", elevated: true },
];

/** ليه الحالة دي مش في القايمة — نص صريح بدل غياب صامت (زي `UNFIXABLE_HERE_AR`) */
export const STATUS_NOT_MANUAL_AR: Record<string, string> = {
  frequent_absence:
    "«كتير الغياب» بيحطها السيستم من سجل الحضور — بتتشال لما تحوّل الطالب لحالة تانية، ومش بتتكتب بالإيد.",
  new: "«جديد» بتتحدد من رحلة التسجيل.",
  // ⛔ **مفيش سطر واحد في السيستم بيكتب `under_evaluation`** — اتأكدنا
  //    بالبحث في الباك إند كله. النص القديم («بتتحدد من شاشة التقييم») كان
  //    بيبعت الموظفة تدوّر على شاشة عمرها ما هتعمل ده، وتستنى حاجة مش جاية.
  //    الصدق هنا أرخص من الانتظار.
  under_evaluation:
    "«تحت التقييم» موجودة في السيستم بس لسه مفيش حاجة بتحطها — لا يدوي ولا تلقائي. استعمل «جديد» لحد ما تتوصل.",
};

/**
 * قنوات التواصل — **مصدر واحد** للمنتقي وللعرض.
 * النصوص مطابقة لـ`channelAr` بالحرف: كتالوجان منفصلان معناهم قناة تتبعت
 * وما تتعرضش، أو اسمان لنفس القناة في نفس الشاشة.
 * ⚠️ `portal` مش هنا عمدًا — دي قناة **قراءة** لداتا مستوردة، مش قناة الموظفة.
 */
export const CONTACT_CHANNEL_OPTIONS: readonly { value: string; label_ar: string }[] = [
  { value: "call", label_ar: "كول" },
  { value: "whatsapp", label_ar: "واتساب" },
  { value: "telegram", label_ar: "تليجرام" },
  { value: "email", label_ar: "إيميل" },
  { value: "meeting", label_ar: "مقابلة" },
];

/** اتجاه القيد — «إحنا كلّمناهم» ولا «هم كلّمونا». ده نص المعنى في السجل. */
export function directionAr(d: string | null): string | null {
  switch (d) {
    case "out":
      return "منّا لولي الأمر";
    case "in":
      return "من ولي الأمر";
    default:
      return null;
  }
}

/** ترجمة كود اللغة — كتالوج مقفول. الكود المجهول بيتقال زي ما هو مش بيتبلع. */
export function languageAr(code: string | null | undefined): string | null {
  if (!code) return null;
  const found = LANGUAGE_OPTIONS.find((o) => o.value === code);
  if (found) return found.label_ar;
  console.error(`[registry] لغة مش في الكتالوج: ${code}`);
  return code;
}

export type PatchResult<T> = { row: T; toastAr: string };

/**
 * ⚠️ `version` إجباري — الباك إند بيرمي 428 من غيره.
 *    والرد بيرجّع النسخة الجديدة، فالشاشة لازم تستبدل السجل كله مش تدمج.
 */
export async function patchStudent(
  studentId: string,
  version: number,
  payload: Record<string, string>,
): Promise<PatchResult<StudentRow>> {
  const path = `/students/${studentId}`;
  const res = await api.patch<unknown>(path, payload, { ifMatch: version });
  const raw = asObject(res.data, path);
  return { row: studentRow(raw, path), toastAr: str(raw["toast_ar"]) || "اتحفظ" };
}

export async function patchFamily(
  familyId: string,
  version: number,
  payload: Record<string, string>,
): Promise<PatchResult<FamilyRow>> {
  const path = `/families/${familyId}`;
  const res = await api.patch<unknown>(path, payload, { ifMatch: version });
  const raw = asObject(res.data, path);
  return { row: familyRow(raw, path), toastAr: str(raw["toast_ar"]) || "اتحفظ" };
}

/**
 * وضع/شيل تصنيف على **الأسرة** بسبب مكتوب.
 *
 * ⚠️ النسخة المبعوتة لازم تكون نسخة **الأسرة** مش نسخة الطالب — الشاشة
 *    ماسكة الاتنين مع بعض، وإرسال نسخة الطالب بيرجّع 409 «حد سبقك»
 *    والموظفة بتعيد التحميل وتلاقي كل حاجة سليمة وتحاول تاني وتفشل تاني.
 */
export async function setFamilyTag(
  familyId: string,
  version: number,
  body: { tag: string; action: "add" | "remove"; reason_ar: string },
): Promise<PatchResult<FamilyRow>> {
  const path = `/families/${familyId}/tags`;
  const res = await api.post<unknown>(path, body, { ifMatch: version });
  const raw = asObject(res.data, path);
  return { row: familyRow(raw, path), toastAr: str(raw["toast_ar"]) || "التصنيف اتغيّر" };
}

/**
 * تغيير حالة الطالب بسبب مكتوب.
 * ⚠️ `version` إجباري — الباك إند بيرمي 428 من غيره، و`api.post` بياخد
 *    `ifMatch` زي `api.patch` بالظبط (نفس `RequestOptions`).
 */
export async function setStudentStatus(
  studentId: string,
  version: number,
  body: { status: string; reason_ar: string },
): Promise<PatchResult<StudentRow>> {
  const path = `/students/${studentId}/status`;
  const res = await api.post<unknown>(path, body, { ifMatch: version });
  const raw = asObject(res.data, path);
  return { row: studentRow(raw, path), toastAr: str(raw["toast_ar"]) || "الحالة اتغيّرت" };
}

export type NewContact = {
  channel: string;
  direction: "in" | "out";
  summaryAr: string;
  parentReplyAr?: string | null;
  studentId?: string | null;
  /** وقت التواصل الحقيقي لو حصل قبل التسجيل. فاضي = دلوقتي. */
  occurredAt?: string | null;
};

/**
 * تسجيل تواصل على **أسرة** — والولد تفصيلة اختيارية جوّه القيد.
 *
 * ⚠️ مفيش `ifMatch` هنا: القفل التفاؤلي بيتبني على نسخة سجل موجود، والإدخال
 *    مالوش نسخة. الحماية من التكرار في اللوحة (زرار مقفول وقت الإرسال) —
 *    والقيد المكرر **ما ينفعش يتشال** (مفيش حذف لسجل التواصل).
 *
 * ⚠️ الحقول الاختيارية بتتبعت `undefined` مش `null`: الجسم في الباك إند
 *    `extra="forbid"` ومعاه `max_length`، و`null` صريح بيدخل في فحص مالوش لزوم.
 */
export async function logFamilyContact(
  familyId: string,
  body: NewContact,
): Promise<{ row: ContactRow; toastAr: string }> {
  const path = `/families/${familyId}/contact-log`;
  const res = await api.post<unknown>(path, {
    channel: body.channel,
    direction: body.direction,
    summary_ar: body.summaryAr,
    parent_reply_ar: body.parentReplyAr?.trim() || undefined,
    student_id: body.studentId || undefined,
    occurred_at: body.occurredAt || undefined,
  });
  const raw = asObject(res.data, path);
  return {
    row: contactRow(raw, path),
    toastAr: str(raw["toast_ar"]) || "اتسجّل إنك كلّمتهم",
  };
}

/* ══════════════════════════════════════════════════════════════════
   نصوص عرض — ترجمة كتالوجات ثابتة، مش اختراع قيم
   ══════════════════════════════════════════════════════════════════ */

/** لون الشارة تبع حالة الطالب — لون واحد + نقطة واحدة لكل حالة */
export function studentTone(status: string | null): string {
  switch (status) {
    case "active":
      return "okc";
    case "new":
    case "under_evaluation":
      return "info";
    case "frequent_absence":
    case "suspended_by_academy":
    case "withdrawn":
      return "late";
    case "paused":
    case "financial_hold":
      return "soon";
    default:
      return "ghost";
  }
}

export function teacherTone(status: string | null): string {
  switch (status) {
    case "active":
    case "approved":
      return "okc";
    case "applicant":
    case "in_journey":
      return "info";
    case "on_leave":
      return "soon";
    case "suspended":
    case "left":
      return "late";
    default:
      return "ghost";
  }
}

export function leaveTone(status: string | null): string {
  switch (status) {
    case "active":
      return "soon";
    case "pending":
      return "info";
    case "ended":
      return "auto";
    case "cancelled":
      return "ghost";
    default:
      return "ghost";
  }
}

/** مصدر تسجيل التوقف — تلقائي من السيستم ولا إدخال بشري */
export type LeaveSource = { label_ar: string; automatic: boolean };

const LEAVE_SOURCES: Record<string, LeaveSource> = {
  teacher_leave_cascade: { label_ar: "تلقائي من إجازة المعلم", automatic: true },
  portal_suspend: { label_ar: "تلقائي من البورتال", automatic: true },
  accounts_hold: { label_ar: "تلقائي من الحسابات", automatic: true },
  staff_entry: { label_ar: "مسجّلة يدويًا", automatic: false },
  parent_whatsapp: { label_ar: "بلاغ ولي الأمر (واتساب)", automatic: false },
};

export function leaveSource(reportSource: string | null): LeaveSource | null {
  if (!reportSource) return null;
  const known = LEAVE_SOURCES[reportSource];
  if (known) return known;
  // ⛔ ممنوع الفشل الصامت: مصدر مش في الكتالوج بيتقال زي ما هو، ما بيتخفيش
  console.error(`[registry] مصدر توقف مش معروف في الكتالوج: ${reportSource}`);
  return { label_ar: `مصدر مش معروف (${reportSource})`, automatic: false };
}

/** أثر التوقف على رصيد الساعات — ⛔ بلا أي رقم مخترَع */
export function hoursPolicyAr(policy: string | null): string | null {
  switch (policy) {
    case "postpone":
      return "الساعات مؤجّلة — الرصيد ما اتخصمش";
    case "consume":
      return "الساعات اتخصمت من الرصيد";
    case "hold":
      return "الرصيد موقوف";
    default:
      return null;
  }
}

export function channelAr(channel: string | null): string | null {
  switch (channel) {
    case "call":
      return "كول";
    case "whatsapp":
      return "واتساب";
    case "telegram":
      return "تليجرام";
    case "email":
      return "إيميل";
    case "meeting":
      return "مقابلة";
    case "portal":
      return "البورتال";
    default:
      return channel;
  }
}
